import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { StatusBadge, fmtDate } from "../../lib/status";
import { BOOKING_GROUPS } from "./vehicleUtils";

export function VehicleBookingsTab({ bookings }) {
  const [filter, setFilter] = useState("all"); // all | active | completed | cancelled
  const [q, setQ] = useState("");

  const groups = useMemo(() => {
    const byGroup = BOOKING_GROUPS.map((g) => ({
      ...g,
      items: bookings.filter((b) => g.statuses.has(b.status)),
    }));
    return byGroup;
  }, [bookings]);

  const term = q.trim().toLowerCase();
  const filterGroup = (g) => {
    if (filter !== "all" && g.id !== filter) return false;
    return true;
  };
  const filterItems = (items) => {
    if (!term) return items;
    return items.filter((b) =>
      (b.customer_name || "").toLowerCase().includes(term) ||
      (b.booking_reference || "").toLowerCase().includes(term) ||
      (b.customer_email || "").toLowerCase().includes(term)
    );
  };

  const now = new Date();
  const isCurrent = (b) => {
    try {
      const s = new Date(b.start_datetime);
      const e = new Date(b.end_datetime);
      return s <= now && now <= e && ["confirmed", "ongoing", "awaiting_customer_confirmation"].includes(b.status);
    } catch { return false; }
  };

  if (bookings.length === 0) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="vd-bookings-empty">No bookings for this vehicle yet.</div>;
  }
  return (
    <div className="space-y-4" data-testid="vd-bookings">
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3" data-testid="vd-bookings-filters">
        <div className="flex items-center gap-1 p-0.5 rounded-lg bg-slate-100">
          {[
            { id: "all", label: "All" },
            { id: "active", label: "Active & upcoming" },
            { id: "completed", label: "Completed" },
            { id: "cancelled", label: "Cancelled" },
          ].map((opt) => (
            <button
              key={opt.id}
              data-testid={`vd-bookings-filter-${opt.id}`}
              onClick={() => setFilter(opt.id)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                filter === opt.id ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, reference, email…"
            data-testid="vd-bookings-search"
            className="w-full pl-7 pr-3 py-1.5 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F] text-sm"
          />
        </div>
        <span className="text-xs text-slate-500">{bookings.length} total</span>
      </div>
      {groups.filter(filterGroup).every((g) => filterItems(g.items).length === 0) && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="vd-bookings-no-match">
          No bookings match the current filter.
        </div>
      )}
      {groups.filter(filterGroup).map((g) => {
        const items = filterItems(g.items);
        if (items.length === 0) return null;
        return (
          <div key={g.id} className="rounded-xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
              {g.label} <span className="text-slate-400">({items.length})</span>
            </h3>
            <div className="mt-3 divide-y divide-slate-100">
              {items.map((b) => {
                const current = isCurrent(b);
                return (
                  <Link
                    key={b.id}
                    to={`/app/bookings/${b.id}`}
                    data-testid={`vd-booking-${b.id}`}
                    className={`block py-3 -mx-2 px-2 rounded-lg ${current ? "bg-emerald-50/60 ring-1 ring-emerald-200" : "hover:bg-slate-50"}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {b.customer_name}
                          {current && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500 text-white uppercase tracking-wider" data-testid={`vd-booking-current-${b.id}`}>
                              Current
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-mono">{b.booking_reference}</div>
                      </div>
                      <StatusBadge status={b.status} isHold={b.is_hold} changeRequested={b.change_requested}
                                   cancelledBy={b.cancelled_by} cancellationReason={b.cancellation_reason}
                                   reconfirmationRequired={b.reconfirmation_required} />
                    </div>
                    <div className="text-sm text-slate-600 mt-1">{fmtDate(b.start_datetime)} → {fmtDate(b.end_datetime)}</div>
                  </Link>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
